<template>
  <v-dialog v-model="show" width="500" persistent>
    <v-form v-model="valid" @submit.prevent="onSubmit" ref="form">
      <v-card v-if="loading">
        Loading ...
      </v-card>
      <v-card v-else>
        <v-toolbar dark color="primary" height="35">
          <v-toolbar-title>
            Process Parameters
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn small icon color="primary" @click="$emit('close')" v-if="!running"
              ><v-icon>close</v-icon></v-btn
            >
          </v-toolbar-items>
        </v-toolbar>

        <v-card-text>
          <div class="parameterContainer" v-show="!running">
            <div class="parameter">
              <vonap-form-label label="Process Date" v-if="showProcessDate">
                <kendo-datepicker
                  required
                  v-if="showProcessDate"
                  v-model="processDate"
                  :format="'yyyy-MM-dd'"
                  style="width:120px;display:inline-block;"
                />
              </vonap-form-label>
              <v-layout wrap>
                <v-flex xs4 v-if="showFromDate">
                  <vonap-form-label label="From Date">
                    <kendo-datepicker
                      required
                      v-model="fromDate"
                      :format="'yyyy-MM-dd'"
                      :max="toDate"
                      style="width:120px;display:inline-block;"
                    />
                  </vonap-form-label>
                </v-flex>
                <v-flex xs4 v-if="showToDate">
                  <vonap-form-label label="To Date">
                    <kendo-datepicker
                      required
                      v-model="toDate"
                      :format="'yyyy-MM-dd'"
                      :min="fromDate"
                      style="width:120px;display:inline-block;"
                    />
                  </vonap-form-label>
                </v-flex>
              </v-layout>
              <div :key="process.processId" v-for="process in dateListParams" class="parameter">
                <div v-if="process.dateList && !showProcessDate">
                  <kendo-dropdownlist
                    data-text-field="dateText"
                    data-value-field="dateValue"
                    v-model="processDate"
                    @databound="processDate = $event.sender.value()"
                    style="width:auto"
                    ><kendo-datasource
                      slot="kendo-datasource"
                      :transport-read-url="'/api/process/ProcessManager/getDateList'"
                      :transport-read-data="{
                        processId: process.processId,
                        selectedDate: selectedDate,
                      }"
                  /></kendo-dropdownlist>
                </div>
              </div>
            </div>
            <div :key="process.processId" v-for="process in customParams" class="parameter">
              <vonap-form-label :label="process.name">
                <div v-if="process.hasCustomParameter">
                  <kendo-dropdownlist
                    data-text-field="text"
                    data-value-field="value"
                    v-model="customParameter"
                    @databound="customParameter = $event.sender.value()"
                  >
                    <kendo-datasource
                      slot="kendo-datasource"
                      :transport-read-url="'/api/process/ProcessManager/customParameterList'"
                      :transport-read-data="{processId: process.processId}"
                    />
                  </kendo-dropdownlist>
                </div>

                <div v-if="process.fileUploadSetting > 0">
                  <input
                    type="file"
                    required
                    :name="`Process${process.processId}Files`"
                    :multiple="process.fileUploadSetting == 2"
                    accept=" .xls, .xlsx"
                  />
                </div>
              </vonap-form-label>
            </div>
          </div>
          <div v-html="confirmMessage" v-if="needsConfirmation" />
          <div v-html="warningMessage" v-if="warningMessage.length" />
          <div v-if="running">
            <h2>Running...</h2>
            <h3>{{ batchProgressMessage }}</h3>
            <v-progress-linear :value="batchProgress"></v-progress-linear>

            <h3>{{ processProgressMessage }}</h3>
            <v-progress-linear :value="processProgress"> {{ processProgress }}</v-progress-linear>
          </div>
        </v-card-text>
        <v-alert :value="error" type="error">
          {{ errorMessage }}
        </v-alert>
        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn type="submit" color="primary" flat v-if="!running" :disabled="submitting">
            <v-progress-circular indeterminate v-if="submitting" />
            <span v-else>Run</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
<script>
import axios from 'axios'
import VonapFormLabel from '@/components/VonapFormLabel'
import processManagerService from '../../services/process-manager'
import notification from '../../helpers/notification'
import {mapGetters} from 'vuex'

export default {
  name: 'process-parameters',
  props: {
    periodId: {
      type: Number,
      required: true,
    },
    processIds: {
      type: Array,
      required: true,
    },
    value: Boolean,
    selectedDate: {
      type: String,
      required: true,
    },
  },
  async mounted() {
    await processManagerService.start()
    processManagerService.on('ReceiveMessage', this.receiveMessage)
    processManagerService.on('BatchProgress', this.onBatchProgress)
    processManagerService.on('ProcessProgress', this.onProcessProgress)
    processManagerService.on('ProcessCompleted', this.processCompleted)
    processManagerService.on('BatchCompleted', this.batchCompleted)
    processManagerService.on('ProcessError', this.onProcessError)
    const processDate = this.adjustDate(new Date(this.selectedDate))
    this.processDate = processDate
    this.fromDate = processDate
    this.toDate = processDate

    const {data} = await axios.get('/api/process/ProcessManager/getProcessesParameters', {
      params: {
        selectedProcesses: this.processIds,
      },
    })
    this.params = data
    this.loading = false
  },
  beforeDestroy() {
    processManagerService.off('ReceiveMessage', this.receiveMessage)
    processManagerService.off('BatchProgress', this.onBatchProgress)
    processManagerService.off('ProcessProgress', this.onProcessProgress)
    processManagerService.off('ProcessCompleted', this.processCompleted)
    processManagerService.off('BatchCompleted', this.batchCompleted)
    processManagerService.off('ProcessError', this.onProcessError)
    processManagerService.stop()
  },
  computed: {
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    customParams() {
      return this.params.filter(p => p.fileUploadSetting != 0 || p.hasCustomParameter)
    },
    showProcessDate() {
      return this.params.filter(p => p.processDate).length > 0
    },
    showFromDate() {
      return this.params.filter(p => p.fromDate).length > 0
    },
    showToDate() {
      return this.params.filter(p => p.toDate).length > 0
    },
    dateListParams() {
      return this.params.filter(p => p.dateList)
    },
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
  },
  data() {
    return {
      loading: true,
      running: false,
      submitting: false,
      error: false,
      errorMessage: '',
      params: [],
      processDate: null,
      fromDate: null,
      toDate: null,
      valid: false,
      needsConfirmation: false,
      confirmMessage: '',
      warningMessage: '',
      batchProgressMessage: '',
      batchProgress: 0,
      processProgressMessage: '',
      processProgress: 0,
      customParameter: null,
    }
  },
  methods: {
    async onSubmit(e) {
      const data = new FormData(e.target)
      const params = {
        processDate: this.adjustDate(this.processDate),
        periodId: this.periodId,
        processIds: this.processIds,
        isValid: this.needsConfirmation,
        customParameter: this.customParameter,
        clientId: this.clientId,
        fromDate: this.adjustDate(this.fromDate),
        toDate: this.adjustDate(this.toDate),
      }

      const filesValid = [...e.target.querySelectorAll('input[type=file][required]')].every(
        fi => fi.files.length > 0,
      )

      if (!filesValid) {
        this.warningMessage = 'Files are required!'
        return
      }

      this.error = false
      this.errorMessage = ''
      this.warningMessage = ''
      this.submitting = true

      const {data: responseData} = await axios({
        url: '/api/process/ProcessManager/run',
        method: 'post',
        params,
        data,
        headers: {'Content-Type': 'multipart/form-data'},
      })
      this.submitting = false

      if (!responseData.isValid) {
        if (responseData.status == 'Confirm') {
          this.confirmMessage = responseData.htmlMessage
          this.needsConfirmation = true
        } else if (responseData.status == 'Warning') {
          {
            this.warningMessage = responseData.htmlMessage
          }
        }
      }

      if (responseData.running) {
        this.confirmMessage = ''
        this.needsConfirmation = true
        this.running = true
      }
    },
    receiveMessage() {},
    onBatchProgress(message, progress) {
      this.batchProgressMessage = message
      this.batchProgress = progress
    },
    onProcessProgress(message, progress) {
      this.processProgressMessage = message
      this.processProgress = progress
    },
    processCompleted(processName, status) {
      notification(`Process ${processName} finished execution with status ${status}`, 'info')
      this.$emit('processCompleted')
    },
    batchCompleted(status, message) {
      notification(`Batch completed with status ${status || ''}! ${message || ''}`)
      this.running = false
      this.$emit('batchCompleted')
    },
    onProcessError(processName, logMessage) {
      notification(`There was an error running the process '${processName}'`, 'error')
      this.running = false
      this.error = true
      this.errorMessage = logMessage
    },
    adjustDate(date) {
      if (!(date instanceof Date)) return date

      const tzOffset = date.getTimezoneOffset() * 60000
      return new Date(date - tzOffset).toISOString().slice(0, 10)
    },
  },
  components: {
    VonapFormLabel,
  },
}
</script>
<style scoped>
.parameter {
  padding-bottom: 20px;
}
</style>
